import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTournament } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast } from '@/lib/toast';
import type { Tournament } from '@swiss-magic/shared-types';
import { TournamentHubLayout } from './TournamentHubLayout';
import { useTournamentLifecycle, type TournamentLifecycle } from './useTournamentLifecycle';
import { DeleteTournamentDialog } from '@/components/tournament-lifecycle/DeleteTournamentDialog';
import { PublishTournamentDialog } from '@/components/tournament-lifecycle/PublishTournamentDialog';
import { CloseRegistrationDialog } from '@/components/tournament-lifecycle/CloseRegistrationDialog';

// Story 083 — parent route for /tournaments/:id/*. Owns the
// tournament fetch + the layout chrome; sub-pages slot into the
// <Outlet /> and read the loaded tournament via outlet context if
// they need it.
//
// We render the layout shell + Outlet immediately, BEFORE the
// tournament fetch resolves, so sub-page testids are present as
// soon as the route mounts. Tests + users see chrome straight
// away instead of a full-page "Loading…" placeholder gated on
// the network. The header shows a brief title placeholder until
// the name arrives. Sub-pages that depend on the loaded
// tournament (Overview) gate on a null context value.

export interface HubOutletContext {
  /** Null while the parent route is still fetching, or until the
   *  fetch resolves. Sub-pages that depend on the tournament data
   *  should gate on this — most sub-pages have their own fetches
   *  and ignore the context entirely. */
  tournament: Tournament | null;
  reloadTournament: () => Promise<void>;
  /** Lifecycle actions (open/close/reopen registration, publish,
   *  unpublish, delete) shared by the overview panels and sub-pages.
   *  Confirm dialogs render once in the layout route. */
  lifecycle: TournamentLifecycle;
  /** Hub-wide God Mode. One toggle in the header governs every
   *  god-mode action across the hub sub-pages (force-edit settings,
   *  manual pairing edits, voiding results, force-adding players).
   *  Resets when switching to a different tournament. */
  godMode: boolean;
  setGodMode: (on: boolean) => void;
  /** Story 093 — the viewer's role on this tournament. Owner-only
   *  affordances (god mode, delete, lifecycle, settings edit, pairings)
   *  are gated on `role === 'owner'`. Defaults to 'owner' until the
   *  tournament resolves. */
  role: 'owner' | 'co-director';
}

export function TournamentHubLayoutRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('director');
  const [tournament, setTournament] = useState<Tournament | null>(null);
  // Hub-wide God Mode, reset whenever the tournament under the hub
  // changes so it never leaks across tournaments.
  const [godMode, setGodMode] = useState(false);
  useEffect(() => {
    setGodMode(false);
  }, [id]);

  const load = useCallback(
    async (tournamentId: string) => {
      try {
        const tn = await getTournament(tournamentId);
        setTournament(tn);
      } catch (err) {
        const msg = err instanceof ApiError ? err.userMessage : 'Failed to load tournament';
        showErrorToast(msg);
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
          navigate('/dashboard');
        }
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void getTournament(id)
      .then((tn) => {
        if (!cancelled) setTournament(tn);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.userMessage : 'Failed to load tournament';
        showErrorToast(msg);
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
          navigate('/dashboard');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const reloadTournament = id ? () => load(id) : async () => {};
  const lifecycle = useTournamentLifecycle(tournament, reloadTournament, godMode);

  // Story 093 — co-directors load the hub too; gate owner-only
  // affordances on this. Defaults to owner until the fetch resolves.
  const role = tournament?.viewerRole ?? 'owner';

  const ctx: HubOutletContext = {
    tournament,
    reloadTournament,
    lifecycle,
    godMode,
    setGodMode,
    role,
  };

  return (
    <TournamentHubLayout
      tournamentId={id ?? ''}
      tournamentName={tournament?.name ?? t('tournament_hub.loading')}
      godMode={godMode}
      setGodMode={setGodMode}
      role={role}
      game={tournament?.game}
    >
      <Outlet context={ctx} />

      <DeleteTournamentDialog
        target={lifecycle.deleteOpen ? tournament : null}
        onCancel={() => lifecycle.setDeleteOpen(false)}
        onConfirm={lifecycle.confirmDelete}
      />
      <PublishTournamentDialog
        target={lifecycle.publishOpen ? tournament : null}
        withRegistration={lifecycle.publishWithRegistration}
        setWithRegistration={lifecycle.setPublishWithRegistration}
        onCancel={() => {
          lifecycle.setPublishOpen(false);
          lifecycle.setPublishWithRegistration(false);
        }}
        onConfirm={lifecycle.confirmPublish}
      />
      <CloseRegistrationDialog
        target={lifecycle.closeRegOpen ? tournament : null}
        pendingCount={lifecycle.pendingCount}
        onCancel={() => lifecycle.setCloseRegOpen(false)}
        onApprove={() => lifecycle.closeWithPendingAction('approve')}
        onReject={() => lifecycle.closeWithPendingAction('reject')}
      />
    </TournamentHubLayout>
  );
}

// Convenience hook for sub-pages that want the parent-route's
// tournament without re-fetching it.
export function useHubOutletContext(): HubOutletContext {
  return useOutletContext<HubOutletContext>();
}
